/**
 * Form model v2 - a transparent, deterministic heuristic that turns the
 * standings we already sync (points-per-game, goal difference, recent form,
 * home edge) into 1X2 probabilities. No external "AI" service, no cost, no
 * fakery: the UI labels it as a form model in beta. Pure + unit-tested.
 *
 * Tuned for tournaments like the World Cup: a team's season PPG is SHRUNK
 * toward the field average for small samples, so an early 1-2 game group table
 * doesn't produce wild calls - it firms up as more group games are played, and
 * works in the knockouts off the completed group standings. The last-5 form is
 * blended in so recent momentum counts, and the goal-difference tweak is damped
 * while the sample is thin.
 */

export interface TeamFormInput {
  played: number;
  points: number;
  gd: number;
  /** e.g. "W,W,D,L,W" or "WWDLW" - latest results, any separator. */
  form: string | null;
}

export interface MatchProbabilities {
  home: number;
  draw: number;
  away: number;
  /** 'home' | 'draw' | 'away' - the model's pick (highest probability). */
  pick: 'home' | 'draw' | 'away';
}

const HOME_EDGE = 0.30; // ~the historical home advantage in points-per-game
const DRAW_BASE = 0.27;
const DRAW_MIN = 0.16;
const SPREAD = 1.05; // logistic steepness over the strength difference

const PRIOR_PPG = 1.35; // field-average strength prior
const PRIOR_GAMES = 2.5; // pseudo-count: how hard small samples shrink to the prior
const FORM_WEIGHT = 0.3; // weight of last-5 form vs the (shrunk) season PPG

/** Points-per-game from a last-5 W/D/L form string (W=3, D=1, L=0), or null. */
export function formPpg(form: string | null): number | null {
  if (!form) return null;
  const letters = form.toUpperCase().replace(/[^WDL]/g, '').slice(-5).split('');
  if (letters.length === 0) return null;
  const pts = letters.reduce((sum, c) => sum + (c === 'W' ? 3 : c === 'D' ? 1 : 0), 0);
  return pts / letters.length;
}

/**
 * Team strength: season PPG shrunk toward the field average for small samples,
 * blended with recent form, plus a small (sample-damped) goal-difference
 * tiebreaker. Robust when only 1-2 group games have been played.
 */
export function teamStrength(team: TeamFormInput): number {
  const seasonPpg = team.played > 0 ? team.points / team.played : PRIOR_PPG;
  const shrunk =
    (PRIOR_GAMES * PRIOR_PPG + team.played * seasonPpg) / (PRIOR_GAMES + team.played);
  const fLetters = (team.form ?? '').toUpperCase().replace(/[^WDL]/g, '').slice(-5);
  const fppg = formPpg(team.form);
  // Thin form (1-2 results) counts for less - it's a small sample too.
  const formWeight = FORM_WEIGHT * (fLetters.length / 5);
  const base = fppg != null ? (1 - formWeight) * shrunk + formWeight * fppg : shrunk;
  const gdPerGame = team.played > 0 ? team.gd / team.played : 0;
  const confidence = team.played / (team.played + PRIOR_GAMES);
  const gdAdj = Math.max(-0.3, Math.min(0.3, gdPerGame * 0.1)) * confidence;
  return base + gdAdj;
}

/** Deterministic 1X2 probabilities, rounded to whole percents that sum to 100. */
export function predictMatch(
  home: TeamFormInput | null,
  away: TeamFormInput | null,
): MatchProbabilities {
  const sHome = teamStrength(home ?? { played: 0, points: 0, gd: 0, form: null }) + HOME_EDGE;
  const sAway = teamStrength(away ?? { played: 0, points: 0, gd: 0, form: null });
  const diff = sHome - sAway;

  const pDraw = Math.max(DRAW_MIN, DRAW_BASE - 0.035 * Math.abs(diff));
  const pHomeShare = 1 / (1 + Math.exp(-diff * SPREAD));
  const rest = 1 - pDraw;

  let homePct = Math.round(rest * pHomeShare * 100);
  let drawPct = Math.round(pDraw * 100);
  let awayPct = 100 - homePct - drawPct;
  // Guard the rounding edges so every segment stays visible and valid.
  if (awayPct < 2) {
    homePct -= 2 - awayPct;
    awayPct = 2;
  }
  if (homePct < 2) {
    awayPct -= 2 - homePct;
    homePct = 2;
  }

  const pick: MatchProbabilities['pick'] =
    homePct >= awayPct && homePct >= drawPct ? 'home' : awayPct >= drawPct ? 'away' : 'draw';

  return { home: homePct, draw: drawPct, away: awayPct, pick };
}

/** Normalize a standings form string to compact "W W D L W" style (max 5). */
export function compactForm(form: string | null | undefined): string | null {
  if (!form) return null;
  const letters = form
    .toUpperCase()
    .replace(/[^WDL]/g, '')
    .slice(-5)
    .split('');
  return letters.length > 0 ? letters.join('-') : null;
}
